import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Security,
  ContentCopy,
  Download,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import { LoggerService } from '../../services/LoggerService';

interface TwoFactorSetupData {
  qr_code: string;
  secret: string;
  backup_codes: string[];
  manual_entry_key: string;
}

interface TwoFactorStatus {
  enabled: boolean;
  setup_complete: boolean;
  has_backup_codes: boolean;
}

const TwoFactorSetup: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [setupData, setSetupData] = useState<TwoFactorSetupData | null>(null);
  const [loading, setLoading] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');

  // Load 2FA status on component mount
  useEffect(() => {
    loadTwoFactorStatus();
  }, []);

  const loadTwoFactorStatus = async () => {
    try {
      const response = await api.get('/2fa/status');
      setStatus(response.data);
    } catch (error: any) {
      LoggerService.error('Error loading 2FA status:', error);
      enqueueSnackbar('Failed to load 2FA status', { variant: 'error' });
    }
  };

  const startSetup = async () => {
    try {
      setLoading(true);
      const response = await api.get('/2fa/setup');
      setSetupData(response.data);
      setActiveStep(1);
      enqueueSnackbar('2FA setup started. Please scan the QR code.', { variant: 'info' });
    } catch (error: any) {
      LoggerService.error('Error starting 2FA setup:', error);
      enqueueSnackbar('Failed to start 2FA setup', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const verifySetup = async () => {
    try {
      setLoading(true);
      await api.post('/2fa/verify-setup', { code: verificationCode });
      setActiveStep(2);
      enqueueSnackbar('2FA setup completed successfully!', { variant: 'success' });
      loadTwoFactorStatus();
    } catch (error: any) {
      LoggerService.error('Error verifying 2FA setup:', error);
      enqueueSnackbar('Invalid verification code', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const disableTwoFactor = async () => {
    try {
      setLoading(true);
      await api.post('/2fa/disable', {
        password: disablePassword,
        code: disableCode,
      });
      enqueueSnackbar('2FA has been disabled', { variant: 'success' });
      loadTwoFactorStatus();
      setDisablePassword('');
      setDisableCode('');
    } catch (error: any) {
      LoggerService.error('Error disabling 2FA:', error);
      enqueueSnackbar('Failed to disable 2FA', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // const regenerateBackupCodes = async () => {
  //   try {
  //     setLoading(true);
  //     const response = await api.post('/2fa/regenerate-backup-codes', {
  //       code: verificationCode,
  //     });
  //     setSetupData(prev => prev ? { ...prev, backup_codes: response.data.backup_codes } : null);
  //     enqueueSnackbar('Backup codes regenerated', { variant: 'success' });
  //   } catch (error: any) {
  //     LoggerService.error('Error regenerating backup codes:', error);
  //     enqueueSnackbar('Failed to regenerate backup codes', { variant: 'error' });
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    enqueueSnackbar('Copied to clipboard', { variant: 'success' });
  };

  const downloadBackupCodes = () => {
    if (!setupData?.backup_codes) return;
    
    const content = `AmudFin - Two-Factor Authentication Backup Codes\n\n${setupData.backup_codes.map((code, i) => `${i + 1}. ${code}`).join('\n')}\n\nKeep these codes safe! Each can only be used once.`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'amudfin-2fa-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // const steps = [
  //   'Scan QR Code',
  //   'Verify Setup',
  //   'Save Backup Codes',
  // ];

  if (!status) {
    return <Box>Loading 2FA status...</Box>;
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <Security sx={{ mr: 1 }} />
            <Typography variant="h6">Two-Factor Authentication</Typography>
            <Chip
              label={status.enabled ? 'Enabled' : 'Disabled'}
              color={status.enabled ? 'success' : 'default'}
              sx={{ ml: 2 }}
            />
          </Box>

          {status.enabled ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Two-factor authentication is enabled and protecting your account.
              </Alert>

              <Box display="flex" gap={2} mb={2}>
                <Button
                  variant="outlined"
                  onClick={() => setShowBackupCodes(true)}
                  disabled={!status.has_backup_codes}
                >
                  View Backup Codes
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setActiveStep(0)}
                >
                  Disable 2FA
                </Button>
              </Box>

              {/* Disable 2FA Dialog */}
              <Dialog open={activeStep === 0 && status.enabled} onClose={() => setActiveStep(-1)}>
                <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
                <DialogContent>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Disabling 2FA will make your account less secure. Are you sure?
                  </Alert>
                  <TextField
                    fullWidth
                    label="Current Password"
                    type="password"
                    value={disablePassword}
                    onChange={(e) => setDisablePassword(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="2FA Code"
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value)}
                    placeholder="Enter 6-digit code from your authenticator app"
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setActiveStep(-1)}>Cancel</Button>
                  <Button
                    onClick={disableTwoFactor}
                    color="error"
                    disabled={loading || !disablePassword || !disableCode}
                  >
                    Disable 2FA
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Backup Codes Dialog */}
              <Dialog open={showBackupCodes} onClose={() => setShowBackupCodes(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Backup Codes</DialogTitle>
                <DialogContent>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Use these codes to access your account if you lose your authenticator device.
                    Each code can only be used once.
                  </Alert>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace' }}>
                      {setupData?.backup_codes?.map((code, i) => `${i + 1}. ${code}`).join('\n')}
                    </Typography>
                  </Paper>
                </DialogContent>
                <DialogActions>
                  <Button onClick={downloadBackupCodes}>
                    <Download sx={{ mr: 1 }} />
                    Download
                  </Button>
                  <Button onClick={() => setShowBackupCodes(false)}>Close</Button>
                </DialogActions>
              </Dialog>
            </Box>
          ) : (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Add an extra layer of security to your account by enabling two-factor authentication.
              </Alert>

              <Button
                variant="contained"
                onClick={startSetup}
                disabled={loading}
                startIcon={<Security />}
              >
                Enable Two-Factor Authentication
              </Button>

              {/* Setup Stepper */}
              {setupData && (
                <Box mt={3}>
                  <Stepper activeStep={activeStep} orientation="vertical">
                    <Step>
                      <StepLabel>Scan QR Code</StepLabel>
                      <StepContent>
                        <Box textAlign="center">
                          <img
                            src={setupData.qr_code}
                            alt="2FA QR Code"
                            style={{ maxWidth: '200px', marginBottom: '16px' }}
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                          </Typography>
                          <Button
                            variant="outlined"
                            onClick={() => copyToClipboard(setupData.manual_entry_key)}
                            startIcon={<ContentCopy />}
                          >
                            Copy Manual Entry Key
                          </Button>
                        </Box>
                        <Box mt={2}>
                          <Button
                            variant="contained"
                            onClick={() => setActiveStep(1)}
                            sx={{ mr: 1 }}
                          >
                            Next
                          </Button>
                        </Box>
                      </StepContent>
                    </Step>

                    <Step>
                      <StepLabel>Verify Setup</StepLabel>
                      <StepContent>
                        <Box>
                          <TextField
                            fullWidth
                            label="Verification Code"
                            value={verificationCode}
                            onChange={(e) => setVerificationCode(e.target.value)}
                            placeholder="Enter 6-digit code from your authenticator app"
                            sx={{ mb: 2 }}
                          />
                          <Button
                            variant="contained"
                            onClick={verifySetup}
                            disabled={loading || verificationCode.length !== 6}
                            sx={{ mr: 1 }}
                          >
                            Verify & Enable
                          </Button>
                          <Button onClick={() => setActiveStep(0)}>
                            Back
                          </Button>
                        </Box>
                      </StepContent>
                    </Step>

                    <Step>
                      <StepLabel>Save Backup Codes</StepLabel>
                      <StepContent>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Important: Save these backup codes!
                          </Typography>
                          <Typography variant="body2">
                            These codes can be used to access your account if you lose your authenticator device.
                            Each code can only be used once.
                          </Typography>
                        </Alert>

                        <Paper sx={{ p: 2, bgcolor: 'background.default', mb: 2 }}>
                          <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace' }}>
                            {setupData.backup_codes.map((code, i) => `${i + 1}. ${code}`).join('\n')}
                          </Typography>
                        </Paper>

                        <Box display="flex" gap={1}>
                          <Button
                            variant="outlined"
                            onClick={downloadBackupCodes}
                            startIcon={<Download />}
                          >
                            Download
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={() => copyToClipboard(setupData.backup_codes.join('\n'))}
                            startIcon={<ContentCopy />}
                          >
                            Copy All
                          </Button>
                        </Box>

                        <Box mt={2}>
                          <Button
                            variant="contained"
                            onClick={() => {
                              setActiveStep(-1);
                              setSetupData(null);
                              setVerificationCode('');
                            }}
                          >
                            Complete Setup
                          </Button>
                        </Box>
                      </StepContent>
                    </Step>
                  </Stepper>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default TwoFactorSetup;
